<template>
    <div class="blog-page" id="main-wrapper">

        <NavbarDark/>

        <div class="page-title">
            <div class="container">
                <div class="row">
                    <div class="col-lg-12 col-md-12">
                        <h2 class="ipt-title">{{$t('Blog Detail')}}</h2>
                        <span class="ipn-subtitle">{{$t('See Our Latest Articles & News')}}</span>
                    </div>
                </div>
            </div>
        </div>
        <section class="gray-simple">
            <div class="container">
                <div class="row">
                    <div class="col-lg-8 col-md-12 col-sm-12 col-12">
                        <div class="blog-details single-post-item format-standard">
                            <div class="post-details">
                            
                                <div class="post-featured-img">
                                    <img
                                        v-if="blog?.image"
                                        class="img-fluid"
                                        :src="blog.image"
                                        alt="blog image"
                                    />
                                </div>
                                
                                <div class="title-row">
                                    <h2 class="post-title">
                                        {{ blog && blog.title ? blog.title : '' }}
                                    </h2>
                                    <span>
                                        {{$t("Visitor Count")}} - {{ visitorCount }}
                                    </span>
                                </div>
                                <p>{{blog && blog.short_description ? blog.short_description : ''}}</p>
                                <p v-html="blog && blog.long_description ? blog.long_description : ''"></p>
                                <div class="post-bottom-meta">
                                    <div class="post-tags">
                                        <h4 class="pbm-title">{{$t('Related Tags')}}</h4>
                                        <ul class="list">
                                            <li class="me-1"  v-for="tag in blog?.tags" :key="tag.id"><router-link to="">{{ tag.name }}</router-link></li>
                                        </ul>
                                    </div>
                                    
                                </div>
                                <div class="single-post-pagination">
                                    <div class="prev-post" v-if="previousBlogId">
                                        <router-link :to="`/${locale}/blog-detail/${previous_slug}`">
                                            <div class="title-with-link">
                                                <span class="intro">{{$t('Prev Blog')}}</span>
                                            </div>
                                        </router-link>
                                    </div>
                                    <div class="post-pagination-center-grid">
                                        <router-link to="/blog"><i class="ti ti-layout-grid3"></i></router-link>
                                    </div>
                                    <div class="next-post" v-if="nextBlogId">
                                        <router-link :to="`/${locale}/blog-detail/${next_slug}`">
                                            <div class="title-with-link">
                                                <span class="intro">{{$t('Next Blog')}}</span>
                                            </div>
                                        </router-link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div class="col-lg-4 col-md-12 col-sm-12 col-12">
                        <BlogSidebar :categories="blog?.categories"/>
                    </div>
                </div>
            </div>
        </section>

        <FooterTop :bg="'theme-bg'"/>

        <FooterDark/>

        <ScrollToTop/>
    </div>
</template>

<script setup>
import BlogSidebar from '@/components/blog-sidebar.vue';
import FooterDark from '@/components/footer/footer-dark.vue'
import FooterTop from '@/components/footer/footer-top.vue'
import NavbarDark from '@/components/navbar/navbar-dark.vue';
import ScrollToTop from '@/components/scroll-to-top.vue'
import { ref, onMounted, watch, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import { useI18n } from "vue-i18n";
import { useHead } from '@vueuse/head'

const blog = ref(null);
const previousBlogId = ref(null);
const previous_slug = ref(null);
const nextBlogId = ref(null);
const next_slug = ref(null);
const route = useRoute();
const { locale } = useI18n();
const visitorCount = ref(0);
const blogSlug = computed(() => route.params.slug)
const router = useRouter()
const fetchBlog = async () => {
    if (!blogSlug.value) return
    try {
        const response = await axios.get(`blogs/show-by-slug/${blogSlug.value}?lang=${locale.value}`);
        if (!response.data?.data?.id) {
            router.replace({name: "NotFound"})
        return
        }

        const blogData = response.data.data;

        blog.value = {
        id: blogData.id,
        slug: blogData.slug || {},
        image: blogData.image || "",
        title: blogData.title || "",
        short_description: blogData.short_description || "",
        long_description: blogData.long_description || "",
        seo_title: blogData.seo_title || "",
        seo_description: blogData.seo_description || "",
        seo_image: blogData.seo_image || "",
        focus_keywords: blogData.focus_keywords || [],
        tags: blogData.tags || [],
        categories: blogData.categories || []
        };
        // console.log('dat',response.data.data);
        
        previousBlogId.value = response.data.previous_blog?.id || null;
        nextBlogId.value = response.data.next_blog?.id || null;
        previous_slug.value = response.data.previous_blog?.slug || null;
        next_slug.value = response.data.next_blog?.slug || null;
    } catch (error) {
        console.error('Error fetching blog details:', error);
    }
};


///// SEO && Google
const baseUrl = 'https://ruqm.me'

const canonicalUrl = computed(() => {
  if (!blog.value?.slug?.[locale.value]) return ''
  return `${baseUrl}/${locale.value}/blog-detail/${blog.value.slug[locale.value]}`
})

const hreflangLinks = computed(() => {
  if (!blog.value?.slug) return []

  return Object.entries(blog.value.slug).map(([lang, slug]) => ({
    rel: 'alternate',
    hreflang: lang,
    href: `${baseUrl}/${lang}/blog-detail/${slug}`
  }))
})

const xDefault = computed(() => {
  const enSlug = blog.value?.slug?.en
  if (!enSlug) return null

  return {
    rel: 'alternate',
    hreflang: 'x-default',
    href: `${baseUrl}/en/blog-detail/${enSlug}`
  }
})

useHead(() => {
  if (!blog.value?.id) return {}

  return {
    title: blog.value.seo_title || blog.value.title,
    meta: [
      {
        name: 'description',
        content: blog.value.seo_description || ''
      },
      {
        name: 'keywords',
        content: blog.value.focus_keywords?.join(', ') || ''
      },
      { property: 'og:type', content: 'event' },
      { property: 'og:url', content: canonicalUrl.value },
      { property: 'og:title', content: blog.value.seo_title },
      { property: 'og:description', content: blog.value.seo_description },
      { property: 'og:image', content: blog.value.seo_image },

      // Twitter
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: blog.value.seo_title },
      { name: 'twitter:description', content: blog.value.seo_description },
      { name: 'twitter:image', content: blog.value.seo_image },
    ],
    link: [
      { rel: 'canonical', href: canonicalUrl.value },
      ...hreflangLinks.value,
      ...(xDefault.value ? [xDefault.value] : [])
    ],
    script: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "name": blog.value.title,
          "description": blog.value.seo_description,
          "image": blog.value.seo_image,
          "url": `https://ruqm.me${route.path}`
        }),
        key: "schema"
      }
    ]
  }
})

async function initBlog() {
  blog.value = null
  visitorCount.value = 0

  await fetchBlog()

  if (blog.value?.id) {
    await axios.post(`/visitor/blog/${blog.value.id}`)
    await fetchVisitorCount()
  }
}

onMounted(() => {
  if (route.params.slug) {
    initBlog()
  }
})

watch(
  () => route.params.slug,
  initBlog
)
const fetchVisitorCount = async () => {
    if (!blog.value.id) return
    try {
        
        const response = await axios.get(`/visitor/blog/${blog.value.id}`);
        visitorCount.value = response.data.visitor_count;     
    } catch (error) {
        console.error('Error fetching visitor count:', error);
    }
};

watch(locale, async (newLocale) => {

    if (!blog.value?.slug) return;

    const localizedSlug = blog.value.slug[newLocale];
    if (!localizedSlug) {
        await router.push({ name: 'NotFound' });
        return;
    }

    const newPath = `/${newLocale}/blog-detail/${localizedSlug}`;

    if (route.fullPath !== newPath) {
        await router.replace(newPath);
    }
});
</script>

<style scoped>
.title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>